import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Lock,
  Eye,
  EyeOff,
  Send,
  Loader2,
  Plus,
  Wand2,
  X,
  Copy,
  Check,
  RefreshCw,
} from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ShareLink } from "@/components/ShareLink";
import { useNoteUpload } from "@/hooks/useNoteUpload";
import { useServerConfig } from "@/hooks/useServerConfig";
import { cn, formatDuration, formatBytes } from "@/lib/utils";

type PasswordSubTab = "share" | "generator";

type PasswordEntry = {
  label: string;
  ip: string;
  username: string;
  value: string;
};

type PasswordGroup = {
  id: string;
  labelKey: string;
  chars: string;
};

const MIN_PASSWORD_LENGTH = 1;
const MAX_PASSWORD_LENGTH = 60;
const DEFAULT_PASSWORD_LENGTH = 12;

const PASSWORD_GROUPS: PasswordGroup[] = [
  {
    id: "upper",
    labelKey: "passwordGenerator.uppercase",
    chars: "ABCDEFGHIJKLMNOPQRSTUVWXYZ",
  },
  {
    id: "lower",
    labelKey: "passwordGenerator.lowercase",
    chars: "abcdefghijklmnopqrstuvwxyz",
  },
  {
    id: "numbers",
    labelKey: "passwordGenerator.numbers",
    chars: "0123456789",
  },
  {
    id: "special",
    labelKey: "passwordGenerator.special",
    chars: "#$%&@^`~.,:;\"'\\/|_-<>*+!?={[()]}",
  },
];

const DEFAULT_SELECTED_GROUPS = PASSWORD_GROUPS.map((group) => group.id);

function getRandomInt(max: number): number {
  if (max <= 0) {
    throw new Error("Intervalo inválido para geração da senha.");
  }

  const cryptoObj = globalThis.crypto;

  if (!cryptoObj?.getRandomValues) {
    throw new Error("Seu navegador não disponibilizou crypto.getRandomValues().");
  }

  const array = new Uint32Array(1);
  const range = 0x100000000;
  const maxUnbiased = range - (range % max);
  let value = 0;

  do {
    cryptoObj.getRandomValues(array);
    value = array[0] ?? 0;
  } while (value >= maxUnbiased);

  return value % max;
}

function randomChar(chars: string): string {
  return chars.charAt(getRandomInt(chars.length));
}

function clampPasswordLength(value: number): number {
  if (!Number.isFinite(value)) return DEFAULT_PASSWORD_LENGTH;
  return Math.min(
    MAX_PASSWORD_LENGTH,
    Math.max(MIN_PASSWORD_LENGTH, Math.floor(value)),
  );
}

function parsePasswordLength(value: string): number {
  return clampPasswordLength(Number(value));
}

function generatePassword({
  length,
  selectedGroups,
}: {
  length: number;
  selectedGroups: PasswordGroup[];
}): string {
  if (selectedGroups.length === 0) {
    throw new Error("passwordGenerator.selectOneType");
  }

  const allChars = selectedGroups.map((group) => group.chars).join("");

  if (!allChars) {
    throw new Error("passwordGenerator.noCharacters");
  }

  let password = "";

  for (let i = 0; i < length; i++) {
    password += randomChar(allChars);
  }

  return password;
}

function getPasswordStrength(length: number): {
  labelKey: string;
  className: string;
} {
  if (length <= 6) {
    return {
      labelKey: "passwordGenerator.weak",
      className: "text-red-500",
    };
  }

  if (length <= 11) {
    return {
      labelKey: "passwordGenerator.medium",
      className: "text-yellow-500",
    };
  }

  return {
    labelKey: "passwordGenerator.strong",
    className: "text-green-500",
  };
}

async function copyTextToClipboard(text: string): Promise<void> {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(text);
    return;
  }

  const textarea = document.createElement("textarea");
  textarea.value = text;
  document.body.appendChild(textarea);
  textarea.select();
  document.execCommand("copy");
  textarea.remove();
}

function StandalonePasswordGenerator() {
  const { t } = useTranslation();
  const [passwordLengthInput, setPasswordLengthInput] = useState(
    String(DEFAULT_PASSWORD_LENGTH),
  );
  const [selectedGroupIds, setSelectedGroupIds] = useState<string[]>(
    DEFAULT_SELECTED_GROUPS,
  );
  const [error, setError] = useState("");
  const [copied, setCopied] = useState(false);

  const passwordLength = parsePasswordLength(passwordLengthInput);
  const passwordStrength = getPasswordStrength(passwordLength);

  const [generatedPassword, setGeneratedPassword] = useState(() => {
    try {
      return generatePassword({
        length: DEFAULT_PASSWORD_LENGTH,
        selectedGroups: PASSWORD_GROUPS,
      });
    } catch {
      return "";
    }
  });

  const generateNewPassword = (
    lengthOverride?: number,
    groupIdsOverride?: string[],
  ) => {
    const length = clampPasswordLength(lengthOverride ?? passwordLength);
    const groupIds = groupIdsOverride ?? selectedGroupIds;
    const groups = PASSWORD_GROUPS.filter((group) => groupIds.includes(group.id));

    try {
      setGeneratedPassword(
        generatePassword({
          length,
          selectedGroups: groups,
        }),
      );
      setError("");
      setCopied(false);
    } catch (err) {
      setError(
        err instanceof Error ? t(err.message) : t("passwordGenerator.cannotGenerate"),
      );
    }
  };

  const handleLengthInputChange = (value: string) => {
    const onlyNumbers = value.replace(/\D/g, "");

    if (onlyNumbers === "") {
      setPasswordLengthInput("");
      return;
    }

    const parsed = Number(onlyNumbers);

    if (parsed > MAX_PASSWORD_LENGTH) {
      setPasswordLengthInput(String(MAX_PASSWORD_LENGTH));
      return;
    }

    setPasswordLengthInput(String(parsed));
  };

  const handleLengthInputBlur = () => {
    const normalizedLength = parsePasswordLength(passwordLengthInput);
    setPasswordLengthInput(String(normalizedLength));
  };

  const handleRangeChange = (value: string) => {
    const normalizedLength = parsePasswordLength(value);
    setPasswordLengthInput(String(normalizedLength));
    generateNewPassword(normalizedLength);
  };

  const toggleGroup = (id: string, checked: boolean) => {
    setError("");

    setSelectedGroupIds((previous) => {
      let next: string[];

      if (checked) {
        next = previous.includes(id) ? previous : [...previous, id];
      } else {
        next = previous.filter((groupId) => groupId !== id);
      }

      if (next.length === 0) {
        setError("Selecione pelo menos um tipo de caractere.");
        return previous;
      }

      return next;
    });
  };

  const handleCopy = async () => {
    if (!generatedPassword) return;

    try {
      await copyTextToClipboard(generatedPassword);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setError(t("passwordGenerator.cannotCopy"));
    }
  };

  return (
    <div className="space-y-6">
      <div className="space-y-2">
        <Label>{t("passwordGenerator.generatedPassword")}</Label>

        <div className="flex items-center gap-2">
          <Input
            value={generatedPassword}
            readOnly
            className="font-mono"
            autoComplete="off"
          />

          <Button
            type="button"
            variant="outline"
            size="icon"
            onClick={handleCopy}
            title={copied ? t("common.copied") : t("passwordGenerator.copyPassword")}
          >
            {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
          </Button>

          <Button
            type="button"
            variant="outline"
            size="icon"
            onClick={() => generateNewPassword()}
            title={t("passwordGenerator.generateNew")}
          >
            <RefreshCw className="h-4 w-4" />
          </Button>
        </div>

        <div className="flex items-center justify-between text-xs">
          <span className={cn("font-semibold", passwordStrength.className)}>
            {t("passwordGenerator.passwordStrength", {
              strength: t(passwordStrength.labelKey),
            })}
          </span>
          <span className="text-muted-foreground">
            {t("passwordGenerator.charactersCount", { count: passwordLength })}
          </span>
        </div>
      </div>

      <div className="space-y-3">
        <Label>{t("passwordGenerator.length")}</Label>

        <Input
          type="number"
          min={MIN_PASSWORD_LENGTH}
          max={MAX_PASSWORD_LENGTH}
          value={passwordLengthInput}
          onChange={(event) => handleLengthInputChange(event.target.value)}
          onBlur={handleLengthInputBlur}
        />

        <input
          type="range"
          min={MIN_PASSWORD_LENGTH}
          max={MAX_PASSWORD_LENGTH}
          step={1}
          value={passwordLength}
          onChange={(event) => handleRangeChange(event.target.value)}
          className="w-full accent-current"
        />
      </div>

      <div className="space-y-3">
        <Label>{t("passwordGenerator.usedCharacters")}</Label>

        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
          {PASSWORD_GROUPS.map((group) => (
            <label
              key={group.id}
              className="flex cursor-pointer items-center justify-between rounded-md border bg-muted/30 px-3 py-2 text-sm hover:bg-muted/60"
            >
              <span>{t(group.labelKey)}</span>
              <input
                type="checkbox"
                checked={selectedGroupIds.includes(group.id)}
                onChange={(event) => toggleGroup(group.id, event.target.checked)}
                className="h-4 w-4 accent-current"
              />
            </label>
          ))}
        </div>
      </div>

      {error && (
        <p className="text-sm text-destructive-foreground" role="alert">
          {error}
        </p>
      )}

      <Button
        type="button"
        onClick={() => generateNewPassword()}
        className="w-full"
        size="lg"
      >
        <Wand2 className="mr-2 h-5 w-5" />
        Gerar senha
      </Button>
    </div>
  );
}

export function PasswordForm({ forcePassword = false }: { forcePassword?: boolean }) {
  const { t } = useTranslation();
  const { config } = useServerConfig();
  const noteHook = useNoteUpload();

  const [passwordSubTab, setPasswordSubTab] = useState<PasswordSubTab>("share");
  const [passwords, setPasswords] = useState<PasswordEntry[]>([
    { label: "", ip: "", username: "", value: "" },
  ]);
  const [showValues, setShowValues] = useState<boolean[]>([false]);

  const [expireSec, setExpireSec] = useState<number | null>(null);
  const [maxViews, setMaxViews] = useState<number | null>(null);
  const [notePassword, setNotePassword] = useState("");
  const [showNotePassword, setShowNotePassword] = useState(false);
  const [notePasswordEnabled, setNotePasswordEnabled] = useState(forcePassword);

  if (!config) return null;

  const effectiveExpireSec = expireSec ?? config.noteDefaultExpire;
  const effectiveMaxViews = maxViews ?? config.noteDefaultViews;

  const nonEmpty = passwords.filter((p) => p.value.length > 0);
  const content = JSON.stringify(
    nonEmpty.map((p) => ({
      label: p.label,
      ip: p.ip,
      username: p.username,
      value: p.value,
    })),
  );

  const contentBytes = new TextEncoder().encode(content).length;
  const sizeExceeded = contentBytes > config.noteMaxSize;

  const isSubmitting =
    noteHook.phase === "encrypting" || noteHook.phase === "uploading";

  const canSubmit =
    passwords.some((p) => p.value.length > 0) && !sizeExceeded && !isSubmitting;

  const updatePassword = (index: number, value: string) => {
    setPasswords((prev) =>
      prev.map((p, i) => (i === index ? { ...p, value } : p)),
    );
  };

  const updateLabel = (index: number, label: string) => {
    setPasswords((prev) =>
      prev.map((p, i) => (i === index ? { ...p, label } : p)),
    );
  };

  const updateIp = (index: number, ip: string) => {
    setPasswords((prev) =>
      prev.map((p, i) => (i === index ? { ...p, ip } : p)),
    );
  };

  const updateUsername = (index: number, username: string) => {
    setPasswords((prev) =>
      prev.map((p, i) => (i === index ? { ...p, username } : p)),
    );
  };

  const addField = () => {
    setPasswords((prev) => [
      ...prev,
      { label: "", ip: "", username: "", value: "" },
    ]);
    setShowValues((prev) => [...prev, false]);
  };

  const removeField = (index: number) => {
    if (passwords.length <= 1) return;

    setPasswords((prev) => prev.filter((_, i) => i !== index));
    setShowValues((prev) => prev.filter((_, i) => i !== index));
  };

  const toggleVisibility = (index: number) => {
    setShowValues((prev) => prev.map((v, i) => (i === index ? !v : v)));
  };

  const handleSubmit = () => {
    noteHook.upload({
      content,
      contentType: "password",
      maxViews: effectiveMaxViews,
      expireSec: effectiveExpireSec,
      password: notePasswordEnabled ? notePassword : "",
    });
  };

  const handleNewNote = () => {
    noteHook.reset();
    setPasswords([{ label: "", ip: "", username: "", value: "" }]);
    setShowValues([false]);
    setNotePassword("");
    setNotePasswordEnabled(forcePassword);
    setPasswordSubTab("share");
  };

  if (noteHook.phase === "done" && noteHook.shareLink) {
    return <ShareLink link={noteHook.shareLink} onNewUpload={handleNewNote} />;
  }

  return (
    <Card>
      <CardContent className="space-y-6 pt-6">
        <div className="grid grid-cols-2 rounded-lg border bg-muted p-1">
          <button
            type="button"
            onClick={() => setPasswordSubTab("share")}
            className={cn(
              "inline-flex items-center justify-center gap-2 rounded-md px-3 py-2 text-sm font-medium transition-colors",
              passwordSubTab === "share"
                ? "bg-background text-foreground shadow-sm"
                : "text-muted-foreground hover:text-foreground",
            )}
          >
            <Lock className="h-4 w-4" />{t("password.shareTab")}</button>

          <button
            type="button"
            onClick={() => setPasswordSubTab("generator")}
            className={cn(
              "inline-flex items-center justify-center gap-2 rounded-md px-3 py-2 text-sm font-medium transition-colors",
              passwordSubTab === "generator"
                ? "bg-background text-foreground shadow-sm"
                : "text-muted-foreground hover:text-foreground",
            )}
          >
            <Wand2 className="h-4 w-4" />{t("password.generatorTab")}</button>
        </div>

        {passwordSubTab === "generator" ? (
          <StandalonePasswordGenerator />
        ) : (
          <>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label>{t("password.passwords")}</Label>
                <span
                  className={`text-xs ${
                    sizeExceeded
                      ? "text-destructive-foreground"
                      : "text-muted-foreground"
                  }`}
                >
                  {formatBytes(contentBytes)} / {formatBytes(config.noteMaxSize)}
                </span>
              </div>

              {passwords.map((pw, index) => (
                <div key={index} className="space-y-2">
                  <Input
                    type="text"
                    value={pw.label}
                    onChange={(e) => updateLabel(index, e.target.value)}
                    placeholder={t("password.nameOrUrlPlaceholder", { number: index + 1 })}
                    className="font-mono"
                    disabled={isSubmitting}
                    autoComplete="off"
                  />

                  <Input
                    type="text"
                    value={pw.ip}
                    onChange={(e) => updateIp(index, e.target.value)}
                    placeholder={t("password.ipPlaceholder")}
                    className="font-mono"
                    disabled={isSubmitting}
                    autoComplete="off"
                  />

                  <Input
                    type="text"
                    value={pw.username}
                    onChange={(e) => updateUsername(index, e.target.value)}
                    placeholder={t("password.usernamePlaceholder")}
                    className="font-mono"
                    disabled={isSubmitting}
                    autoComplete="off"
                  />

                  <div className="flex items-center gap-2">
                    <div className="relative flex-1">
                      <Input
                        type={showValues[index] ? "text" : "password"}
                        value={pw.value}
                        onChange={(e) => updatePassword(index, e.target.value)}
                        placeholder={t("password.enterPassword")}
                        className="pr-9 font-mono"
                        disabled={isSubmitting}
                        autoComplete="off"
                      />
                      <button
                        type="button"
                        className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-muted-foreground hover:text-foreground"
                        onClick={() => toggleVisibility(index)}
                      >
                        {showValues[index] ? (
                          <EyeOff className="h-4 w-4" />
                        ) : (
                          <Eye className="h-4 w-4" />
                        )}
                      </button>
                    </div>

                    {passwords.length > 1 && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="shrink-0 text-muted-foreground hover:text-destructive-foreground"
                        onClick={() => removeField(index)}
                        disabled={isSubmitting}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                </div>
              ))}

              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={addField}
                disabled={isSubmitting}
                className="w-full"
              >
                <Plus className="mr-1.5 h-4 w-4" />
                {t("password.addAnother")}
              </Button>

              {sizeExceeded && (
                <p className="text-sm text-destructive-foreground" role="alert">
                  {t("note.tooLarge", {
                    size: formatBytes(config.noteMaxSize),
                  })}
                </p>
              )}
            </div>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label>{t("note.expiry")}</Label>
                <Select
                  value={String(effectiveExpireSec)}
                  onValueChange={(v) => setExpireSec(parseInt(v, 10))}
                  disabled={isSubmitting}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {config.noteExpireOptions.map((sec) => (
                      <SelectItem key={sec} value={String(sec)}>
                        {formatDuration(sec)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>{t("note.maxViews")}</Label>
                <Select
                  value={String(effectiveMaxViews)}
                  onValueChange={(v) => setMaxViews(parseInt(v, 10))}
                  disabled={isSubmitting}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {config.noteViewOptions.map((num) => (
                      <SelectItem key={num} value={String(num)}>
                        {num === 0
                          ? t("note.unlimited")
                          : num === 1
                            ? t("note.burnAfterReading")
                            : String(num)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label className="flex items-center gap-2">
                  <Lock className="h-4 w-4" />
                  {t("upload.password")}
                  {forcePassword && (
                    <span className="text-xs text-muted-foreground">
                      ({t("upload.passwordRequired")})
                    </span>
                  )}
                </Label>

                {!forcePassword && (
                  <Switch
                    checked={notePasswordEnabled}
                    onCheckedChange={setNotePasswordEnabled}
                    disabled={isSubmitting}
                  />
                )}
              </div>

              {notePasswordEnabled && (
                <div className="relative">
                  <Input
                    type={showNotePassword ? "text" : "password"}
                    value={notePassword}
                    onChange={(e) => setNotePassword(e.target.value)}
                    placeholder={t("upload.passwordPlaceholder")}
                    autoComplete="off"
                    disabled={isSubmitting}
                  />
                  <button
                    type="button"
                    className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-muted-foreground hover:text-foreground"
                    onClick={() => setShowNotePassword(!showNotePassword)}
                  >
                    {showNotePassword ? (
                      <EyeOff className="h-4 w-4" />
                    ) : (
                      <Eye className="h-4 w-4" />
                    )}
                  </button>
                </div>
              )}
            </div>

            {noteHook.phase === "error" && noteHook.error && (
              <p className="text-sm text-destructive-foreground" role="alert">
                {noteHook.error}
              </p>
            )}

            <Button
              onClick={handleSubmit}
              disabled={!canSubmit}
              className="w-full"
              size="lg"
            >
              {isSubmitting ? (
                <Loader2 className="mr-2 h-5 w-5 animate-spin" />
              ) : (
                <Send className="mr-2 h-5 w-5" />
              )}
              {isSubmitting ? t("note.creating") : t("note.create")}
            </Button>
          </>
        )}
      </CardContent>
    </Card>
  );
}
